import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mic, MicOff, Send, MapPin, CheckCircle2, XCircle, Sparkles, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * VoiceBookingButton — single 🎙️ CTA at top of homepage.
 * Uses browser SpeechRecognition + Gemini extraction via /api/voice-book/*.
 */
export default function VoiceBookingButton() {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);
  const [chat, setChat] = useState([]);            // [{role, text}]
  const [chatInput, setChatInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const recRef = useRef(null);
  const silenceTimerRef = useRef(null);

  const supportsSR = typeof window !== "undefined"
    && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const startListening = () => {
    if (!supportsSR) {
      toast.error("Voice not supported — use chat below");
      setOpen(true);
      return;
    }
    setOpen(true);
    setTranscript("");
    setOrder(null); setChat([]); setConfirmed(false);
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = "en-IN";
    r.onresult = (e) => {
      let txt = "";
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript + " ";
      setTranscript(txt.trim());
      // auto-stop after 3s of silence
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => stopListening(), 3000);
    };
    r.onerror = (e) => { toast.error("Mic error — try chat"); setListening(false); };
    r.onend = () => setListening(false);
    r.start();
    recRef.current = r;
    setListening(true);
  };

  const stopListening = async () => {
    if (recRef.current) { try { recRef.current.stop(); } catch {} }
    setListening(false);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (transcript.trim().length > 3) await extract(transcript);
  };

  const extract = async (text) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/voice-book/extract`, { transcript: text });
      const d = r.data;
      if (d.ok && d.order?.service) {
        setOrder(d.order);
      } else {
        // trigger fallback chat
        setSessionId(d.session_id);
        setChat([{ role: "ai", text: d.next_question || "What kind of help do you need?" }]);
      }
    } catch (e) { toast.error("Could not parse — try again"); }
    finally { setBusy(false); }
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChat((c) => [...c, { role: "me", text: msg }]);
    setChatInput("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/voice-book/chat`, {
        session_id: sessionId, transcript, answer: msg,
      });
      setSessionId(r.data.session_id);
      if (r.data.order?.service) {
        setOrder(r.data.order);
        setChat((c) => [...c, { role: "ai", text: "Got it! Here's your booking summary 👇" }]);
      } else {
        setChat((c) => [...c, { role: "ai", text: r.data.next_question || "One more thing…" }]);
      }
    } catch (e) { toast.error("Try again"); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    if (!token) { toast.error("Sign up first — quick 30 sec"); window.location.href = "/signup-quick"; return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/voice-book/confirm`, { order },
        { headers: { Authorization: `Bearer ${token}` } });
      setConfirmed(true);
      setOrder({ ...order, booking_id: r.data.booking_id,
                 advance_deducted: r.data.advance_deducted,
                 remaining_due: r.data.remaining_due,
                 notified_mates_count: r.data.notified_mates_count });
      toast.success(`Booked · ₹${r.data.advance_deducted} advance deducted · ${r.data.notified_mates_count || 0} Mates pinged on WhatsApp`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* THE BUTTON */}
      <button data-testid="voice-booking-btn" onClick={startListening}
        className="w-full rounded-3xl p-4 md:p-5 border-2 relative overflow-hidden voice-blink flex items-center justify-between gap-3 group"
        style={{ borderColor: "#F45118",
                 background: "linear-gradient(135deg,#FFF3EA,#FFE8D5,#FFDDC0)" }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full grid place-items-center text-white shadow-lg group-hover:scale-110 transition"
               style={{ background: "linear-gradient(135deg,#F45118,#DC2626)" }}>
            <Mic className="w-5 h-5" />
          </div>
          <div className="text-left">
            <div className="font-display text-lg md:text-2xl font-bold" style={{ color: "#00604A" }}>
              🎙️ Voice Booking — <span style={{ color: "#F45118" }}>Just Speak.</span>
            </div>
            <div className="text-xs" style={{ color: "#667085" }}>One tap for any help — auto, luggage, elder, food pickup, anything.</div>
          </div>
        </div>
        <span className="hidden md:inline text-xs font-bold rounded-full px-3 py-1.5 text-white shadow"
              style={{ background: "#F45118" }}>Tap →</span>
      </button>

      {/* MODAL */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 md:p-6"
               onClick={(e) => e.stopPropagation()} data-testid="voice-book-modal">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full grid place-items-center text-white"
                     style={{ background: "linear-gradient(135deg,#F45118,#DC2626)" }}>
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: "#667085" }}>Voice Booking</div>
                  <div className="font-display text-lg font-bold" style={{ color: "#00604A" }}>Speak your request</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="chip text-xs">✕</button>
            </div>

            {/* Mic strip */}
            <div className="mt-4 rounded-2xl border-2 p-4"
                 style={{ borderColor: listening ? "#F45118" : "#E4E7E5",
                          background: listening ? "#FFF8F1" : "#F7FBF9" }}>
              <div className="flex items-center gap-3">
                <button data-testid="voice-mic-toggle"
                        onClick={() => listening ? stopListening() : startListening()}
                        className="w-11 h-11 rounded-full grid place-items-center text-white shadow-lg"
                        style={{ background: listening ? "#DC2626" : "#00604A" }}>
                  {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>
                <div className="flex-1">
                  <div className="text-xs font-bold" style={{ color: listening ? "#F45118" : "#667085" }}>
                    {listening ? "Listening… speak now" : (transcript ? "Tap mic to re-record" : "Tap mic and start speaking")}
                  </div>
                  <div className="text-sm mt-0.5 italic" style={{ color: "#00604A" }}>
                    {transcript || "e.g. \"auto from Thane station to Kapurwadi at 6 pm\""}
                  </div>
                </div>
              </div>
              {busy && <div className="text-xs mt-2" style={{ color: "#F45118" }}>Understanding…</div>}
            </div>

            {/* Fallback CHAT */}
            {chat.length > 0 && !order && (
              <div className="mt-4 rounded-2xl border p-3 space-y-2" style={{ borderColor: "#E4E7E5" }}
                   data-testid="voice-quick-chat">
                <div className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
                     style={{ color: "#F45118" }}>
                  <MessageCircle className="w-3 h-3" /> Quick Chat
                </div>
                {chat.map((c, i) => (
                  <div key={i} className={`text-sm px-3 py-2 rounded-2xl ${c.role === "me" ? "ml-auto max-w-[80%] text-white" : "max-w-[80%]"}`}
                       style={c.role === "me"
                         ? { background: "#00604A" }
                         : { background: "#FFF3EA", color: "#00604A" }}>
                    {c.text}
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <input data-testid="voice-chat-input"
                         value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                         onKeyDown={(e) => e.key === "Enter" && sendChat()}
                         placeholder="Type your answer…"
                         className="flex-1 border-2 rounded-full px-3 py-2 text-sm outline-none"
                         style={{ borderColor: "#E4E7E5" }} />
                  <button data-testid="voice-chat-send" onClick={sendChat} disabled={busy}
                          className="rounded-full px-3 py-2 text-white text-xs font-bold"
                          style={{ background: "#F45118" }}>
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* ORDER SUMMARY / CONFIRMATION */}
            {order && (
              <div className="mt-4 rounded-2xl border-2 p-4" style={{ borderColor: "#00604A", background: "#F0FDF4" }}
                   data-testid="voice-order-card">
                {!confirmed ? (
                  <>
                    <div className="text-xs uppercase tracking-widest font-bold" style={{ color: "#F45118" }}>Got it. Here is your order.</div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <Field label="Service" value={order.service} />
                      <Field label="Pickup" value={order.pickup || "—"} />
                      <Field label="Destination" value={order.destination || "—"} />
                      <Field label="Date · Time" value={`${order.date || "—"} · ${order.time || "—"}`} />
                      <Field label="Duration" value={order.duration || "—"} />
                      <Field label="Urgency" value={order.urgency || "flexible"} />
                    </div>
                    {order.notes && <div className="mt-2 text-xs italic" style={{ color: "#667085" }}>"{order.notes}"</div>}
                    {(!order.pickup) && (
                      <div className="mt-2 flex gap-2">
                        <button data-testid="use-gps" onClick={() => {
                          if (!navigator.geolocation) return toast.error("GPS unavailable");
                          navigator.geolocation.getCurrentPosition((pos) => {
                            setOrder({ ...order, pickup: `GPS ${pos.coords.latitude.toFixed(4)},${pos.coords.longitude.toFixed(4)}` });
                          });
                        }} className="chip text-[10px]"><MapPin className="w-3 h-3" /> Use GPS</button>
                        <a href={`https://wa.me/919004398004?text=${encodeURIComponent(`Voice booking request:\n\nService: ${order.service}\nDestination: ${order.destination || "—"}\nTime: ${order.time || "—"}\n\nPlease help set pickup.`)}`}
                           target="_blank" rel="noreferrer"
                           className="chip text-[10px]" style={{ color: "#25D366" }}>📲 Send on WhatsApp</a>
                      </div>
                    )}

                    <div className="mt-3 rounded-xl p-3" style={{ background: "#fff", border: "1px dashed #F45118" }}>
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: "#667085" }}>Estimated cost</div>
                      <div className="mt-0.5 flex items-baseline gap-2">
                        {order.discount_pct > 0 && <span className="text-xs line-through" style={{ color: "#667085" }}>₹{order.original_cost}</span>}
                        <span className="font-display text-2xl font-bold" style={{ color: "#00604A" }}>₹{order.final_cost}</span>
                        {order.discount_pct > 0 && <span className="text-[10px] font-bold rounded-full px-2 py-0.5" style={{ background: "#D1FAE5", color: "#065F46" }}>{order.discount_pct}% early-booking off</span>}
                      </div>
                      <div className="text-[10px] mt-1" style={{ color: "#667085" }}>₹100 advance from wallet · balance ₹{Math.max(0, (order.final_cost || 0) - 100)} on completion</div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button data-testid="voice-edit" onClick={() => { setOrder(null); setChat([]); setTranscript(""); }}
                              className="flex-1 py-2.5 rounded-full font-bold border-2 text-sm"
                              style={{ borderColor: "#00604A", color: "#00604A" }}>Edit</button>
                      <button data-testid="voice-approve" onClick={approve} disabled={busy}
                              className="flex-1 py-2.5 rounded-full font-bold text-white text-sm shadow-lg"
                              style={{ background: "#F45118" }}>
                        {busy ? "Booking…" : "Approve & Book"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-2">
                    <CheckCircle2 className="w-10 h-10 mx-auto" style={{ color: "#00604A" }} />
                    <div className="mt-2 font-display text-xl font-bold" style={{ color: "#00604A" }}>Booked!</div>
                    <div className="text-xs mt-1" style={{ color: "#667085" }}>
                      ₹{order.advance_deducted} advance deducted from your HeylpMate Wallet.
                      Remaining ₹{order.remaining_due} due on completion.
                    </div>
                    {order.notified_mates_count > 0 && (
                      <div className="mt-2 text-xs font-bold inline-flex items-center gap-1 rounded-full px-3 py-1"
                           style={{ background: "#D1FAE5", color: "#065F46" }}>
                        💚 {order.notified_mates_count} verified Mate{order.notified_mates_count > 1 ? "s" : ""} pinged on WhatsApp — expect a reply shortly
                      </div>
                    )}
                    <div className="text-[10px] mt-2 font-mono" style={{ color: "#F45118" }}>Ref · {order.booking_id?.slice(0, 12)}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest" style={{ color: "#667085" }}>{label}</div>
      <div className="text-sm font-bold" style={{ color: "#00604A" }}>{value || "—"}</div>
    </div>
  );
}
